import { resolvePatientDiscountAmount } from './patient-discount';

export type DeliveryFeeReason = 'APPLIED' | 'EXEMPT_CLIENT' | 'OVER_THRESHOLD' | 'WAIVED';

/** Reasons the API already stores. WAIVED is display-only and is saved as amount 0 + APPLIED. */
export type StoredDeliveryFeeReason = Exclude<DeliveryFeeReason, 'WAIVED'>;

export const DEFAULT_DELIVERY_FEE_ZAR = 150;
export const DEFAULT_DELIVERY_FEE_THRESHOLD_ZAR = 1500;

export function isDeliveryFeeExemptFlag(value: boolean | number | string | null | undefined): boolean {
  return value === true || value === 1 || value === '1';
}

/** Company is exempt if flagged, or if its linked distributor is flagged. */
export function isCompanyDeliveryFeeExempt(company: {
  delivery_fee_exempt?: boolean | number | string | null;
  distributor?: { delivery_fee_exempt?: boolean | number | string | null } | null;
} | null | undefined): boolean {
  if (!company) return false;
  return (
    isDeliveryFeeExemptFlag(company.delivery_fee_exempt) ||
    isDeliveryFeeExemptFlag(company.distributor?.delivery_fee_exempt)
  );
}

/** Individual is exempt if flagged, or via their distributor / company distributor. */
export function isIndividualDeliveryFeeExempt(individual: {
  delivery_fee_exempt?: boolean | number | string | null;
  distributor?: { delivery_fee_exempt?: boolean | number | string | null } | null;
  company?: {
    delivery_fee_exempt?: boolean | number | string | null;
    distributor?: { delivery_fee_exempt?: boolean | number | string | null } | null;
  } | null;
} | null | undefined): boolean {
  if (!individual) return false;
  return (
    isDeliveryFeeExemptFlag(individual.delivery_fee_exempt) ||
    isDeliveryFeeExemptFlag(individual.distributor?.delivery_fee_exempt) ||
    isCompanyDeliveryFeeExempt(individual.company)
  );
}

export function resolveDeliveryFee(opts: {
  exempt: boolean;
  medicineSubtotalAfterDiscount: number;
  feeZar?: number;
  thresholdZar?: number;
}): { amount: number; reason: DeliveryFeeReason } {
  const fee = Number(opts.feeZar);
  const threshold = Number(opts.thresholdZar);
  const feeZar = Number.isFinite(fee) && fee > 0 ? fee : DEFAULT_DELIVERY_FEE_ZAR;
  const thresholdZar =
    Number.isFinite(threshold) && threshold > 0 ? threshold : DEFAULT_DELIVERY_FEE_THRESHOLD_ZAR;
  const subtotal = Number(opts.medicineSubtotalAfterDiscount);
  const medicineAfterDiscount = Number.isFinite(subtotal) ? subtotal : 0;

  if (opts.exempt) return { amount: 0, reason: 'EXEMPT_CLIENT' };
  if (medicineAfterDiscount >= thresholdZar) return { amount: 0, reason: 'OVER_THRESHOLD' };
  return { amount: feeZar, reason: 'APPLIED' };
}

type DeliveryFeeParty = {
  delivery_fee_exempt?: boolean | number | string | null;
  distributor?: { delivery_fee_exempt?: boolean | number | string | null } | null;
  company?: {
    delivery_fee_exempt?: boolean | number | string | null;
    distributor?: { delivery_fee_exempt?: boolean | number | string | null } | null;
  } | null;
} | null | undefined;

/** Client, patient, doctor, or prescriber (or their distributor) is exempt. */
export function isOrderDeliveryFeeExempt(order: {
  client?: DeliveryFeeParty;
  patient?: DeliveryFeeParty;
  doctor?: DeliveryFeeParty;
  prescriber?: DeliveryFeeParty;
} | null | undefined): boolean {
  if (!order) return false;
  return (
    isCompanyDeliveryFeeExempt(order.client) ||
    isIndividualDeliveryFeeExempt(order.patient) ||
    isIndividualDeliveryFeeExempt(order.doctor) ||
    isIndividualDeliveryFeeExempt(order.prescriber)
  );
}

/**
 * Staff removed the fee. An explicit 0 (not null) with no threshold/exempt
 * reason is that removal. Null means the fee has not been stored yet.
 */
export function isManuallyRemovedDeliveryFee(
  amount: number | string | null | undefined,
  reason: string | null | undefined
): boolean {
  if (reason === 'WAIVED') return true;
  if (reason === 'OVER_THRESHOLD' || reason === 'EXEMPT_CLIENT') return false;
  if (amount === null || amount === undefined || amount === '') return false;
  const n = Number(amount);
  return Number.isFinite(n) && n <= 0;
}

/**
 * Fee for the current medicine lines.
 * Over R1 500 (after patient discount) or an exempt account is always R0,
 * even when a previous save stored R150. A fee that was removed stays off.
 */
export function resolveOrderDeliveryFee(opts: {
  exempt: boolean;
  medicineSubtotalAfterDiscount: number;
  storedAmount?: number | string | null;
  storedReason?: string | null;
  removed?: boolean;
  feeZar?: number;
  thresholdZar?: number;
}): { amount: number; reason: DeliveryFeeReason } {
  const computed = resolveDeliveryFee(opts);
  if (computed.amount === 0) return computed;
  if (
    opts.removed ||
    isManuallyRemovedDeliveryFee(opts.storedAmount, opts.storedReason)
  ) {
    return { amount: 0, reason: 'WAIVED' };
  }
  return computed;
}

type FeeLine = {
  quantity?: number | null;
  total_quantity?: number | null;
  medicineDosagePackageType?: { price?: number | null } | null;
  medicine_dosage_package_type?: { price?: number | null } | null;
};

export function lineMedicineSubtotal(items: FeeLine[] | null | undefined): number {
  if (!items?.length) return 0;
  return items.reduce((sum, item) => {
    const qty = Number(item.quantity ?? item.total_quantity ?? 0);
    const price = Number(
      item.medicineDosagePackageType?.price ??
        item.medicine_dosage_package_type?.price ??
        0
    );
    const q = Number.isFinite(qty) ? qty : 0;
    const p = Number.isFinite(price) ? price : 0;
    return sum + q * p;
  }, 0);
}

export function deliveryFeeForLines(opts: {
  order: {
    client?: DeliveryFeeParty;
    patient?: DeliveryFeeParty;
    doctor?: DeliveryFeeParty;
    prescriber?: DeliveryFeeParty;
    patient_discount_percent?: number | string | null;
    patient_discount_amount?: number | string | null;
    delivery_fee_amount?: number | string | null;
    delivery_fee_reason?: string | null;
  } | null | undefined;
  items: FeeLine[] | null | undefined;
  removed?: boolean;
}): { amount: number; reason: DeliveryFeeReason } {
  const subtotal = lineMedicineSubtotal(opts.items);
  const discount = resolvePatientDiscountAmount(
    subtotal,
    opts.order?.patient_discount_percent,
    opts.order?.patient_discount_amount
  );
  return resolveOrderDeliveryFee({
    exempt: isOrderDeliveryFeeExempt(opts.order),
    medicineSubtotalAfterDiscount: Math.max(0, subtotal - discount),
    storedAmount: opts.order?.delivery_fee_amount,
    storedReason: opts.order?.delivery_fee_reason,
    removed: opts.removed,
  });
}

/**
 * Persistable fields. A removed fee is stored as R0 with reason APPLIED so a
 * later update does not treat it as a normal charge. Threshold and exempt
 * orders that were not removed keep their own reason.
 */
export function deliveryFeeFieldsForSave(opts: {
  fee: { amount: number; reason: DeliveryFeeReason };
  removed?: boolean;
  storedAmount?: number | string | null;
  storedReason?: string | null;
}): {
  delivery_fee_amount: number;
  delivery_fee_reason: StoredDeliveryFeeReason;
} {
  const removed =
    !!opts.removed ||
    opts.fee.reason === 'WAIVED' ||
    isManuallyRemovedDeliveryFee(opts.storedAmount, opts.storedReason);
  if (removed) {
    return { delivery_fee_amount: 0, delivery_fee_reason: 'APPLIED' };
  }
  return {
    delivery_fee_amount: opts.fee.amount,
    delivery_fee_reason: opts.fee.reason === 'WAIVED' ? 'APPLIED' : opts.fee.reason,
  };
}

export function storedDeliveryFeeAmount(
  order: { delivery_fee_amount?: number | string | null } | null | undefined
): number {
  const n = Number(order?.delivery_fee_amount);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function deliveryFeeReasonLabel(reason: string | null | undefined): string {
  if (reason === 'EXEMPT_CLIENT') return 'exempt';
  if (reason === 'OVER_THRESHOLD') return 'order R1 500 or more';
  if (reason === 'WAIVED') return 'removed';
  return '';
}

export function deliveryFeeRowLabel(reason: string | null | undefined): string {
  const extra = deliveryFeeReasonLabel(reason);
  return extra ? `Delivery fee (${extra})` : 'Delivery fee';
}
